import { Component, OnInit, ViewChild } from '@angular/core';
import {
  AppBarAction,
  AppBarActionsService,
  AppBarTitleService,
  FilterParameter,
  LoanStatus,
  MessageService,
  QueryParamsService,
  AppPageService,
  SimpleDialogComponent,
  CommonActionKey,
  HorizontalListData,
  SimpleHorizontalListWidgetConfigData
} from 'common';
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
  AccountPledgeData, AccountPledgeFailedDialogData, AccountPledgeFilters,
  AccountPledgeForm,
  AccountPledgeQueryParams,
  AccountPledgeQueryResult,
  Facility
} from "./account-pledge.model";
import {
  AccountPledgeAdapter,
  accountPledgeDisplayedColumns,
  adaptAccountPledgeParams,
  adaptFilters,
  adaptQueryParams, confirmationDialogData
} from "./account-pledge.data";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { AccountPledgeService } from "./account-pledge.service";
import { EMPTY, forkJoin } from "rxjs";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { ActivatedRoute, Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import {
  AccountPledgeFilterDialogComponent
} from "./account-pledge-filter-dialog/account-pledge-filter-dialog.component";
import {
  AccountPledgeFailedDialogComponent
} from "./account-pledge-failed-dialog/account-pledge-failed-dialog.component";
import { catchError } from 'rxjs/operators';

@UntilDestroy()
@Component({
    selector: 'ifb-account-pledge',
    templateUrl: './account-pledge.component.html',
    styleUrls: ['./account-pledge.component.scss'],
    providers: [AccountPledgeAdapter],
    standalone: false
})
export class AccountPledgeComponent implements OnInit {
  @ViewChild(MatPaginator, {static: true}) matPaginator: MatPaginator;
  @ViewChild(MatSort) matSort: MatSort;

  activeFilters: FilterParameter[] = [];
  accountPledgeQueryResult: AccountPledgeQueryResult;
  displayedColumns = accountPledgeDisplayedColumns;
  facilities: Facility[] = [];
  selectedFacilityId: number;
  selectedLoans: AccountPledgeData[] = [];
  totalCreditLimit: number = 0;
  totalOutstandingBalance: number = 0;
  totalsListItems: HorizontalListData[];
  totalsWidgetConfig: SimpleHorizontalListWidgetConfigData;
  queryParams: AccountPledgeQueryParams;

  form: FormGroup<AccountPledgeForm>;

  readonly LoanStatus = LoanStatus;

  private filters: AccountPledgeFilters;

  constructor(
    private readonly appBarActionsService: AppBarActionsService,
    private readonly appBarTitleService: AppBarTitleService,
    private readonly accountPledgeService: AccountPledgeService,
    private readonly messageService: MessageService,
    private readonly appPageService: AppPageService,
    private readonly queryParamsService: QueryParamsService,
    private readonly route: ActivatedRoute,
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly adapter: AccountPledgeAdapter,
  ) {
    this.setAppBarActions();
    this.form = this.fb.group({
      facilityId: new FormControl<number>(null)
    });
  }

  ngOnInit(): void {
    this.appBarTitleService.title = "Pledging";

    this.appBarActionsService.invoking
      .pipe(untilDestroyed(this))
      .subscribe(this.actionDispatch.bind(this));

    this.setDataFromQueryParams();

    this.initialize();

    this.form.controls.facilityId.valueChanges.pipe(untilDestroyed(this)).subscribe(id => {
      this.selectedFacilityId = id;
      this.filter(true);
    });


    this.route.queryParams.pipe(untilDestroyed(this)).subscribe(params => {
      this.queryParams = adaptAccountPledgeParams(params);
      this.activeFilters = adaptFilters(this.queryParams);
      this.setPaginator();
    });

    this.recalculateTotals();
  }

  activeFilterRemoved(activeFilters: FilterParameter[]): void {
    if (!activeFilters.length) return;
    activeFilters.forEach(item => {
      this.filters[item.id] = undefined;
      this.filter(true);
    });
  }

  filterDialog(): void {
    this.dialog.open(AccountPledgeFilterDialogComponent, {
      data: {queryParams: this.queryParams},
      closeOnNavigation: true,
    }).afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((result: AccountPledgeFilters | boolean) => {
        if (result) {
          this.filters = result as AccountPledgeFilters;
          this.matPaginator.firstPage();
          this.filter(true);
        }
      });
  }

  sortData(): void {
    this.filter(false);
  }

  setPage(): void {
    this.filter(false);
  }

  onChange(event: MatCheckboxChange, loan: AccountPledgeData): void {
    if (event.checked) {
      this.selectedLoans.push(loan);
    } else {
      this.selectedLoans = this.selectedLoans.filter(l => l.id !== loan.id)
    }

    this.recalculateTotals()
  }

  save(): void {
    const selectedLoans = this.selectedLoans;
    if (selectedLoans.length <= 0) {
      this.messageService.error("Please select a loan");
      return;
    }
    if (!this.form.controls.facilityId.value) {
      this.messageService.error("Please select a facility");
      return;
    }

    this.showConfirmationDialog(selectedLoans);
  }

  close(): void {
    this.appPageService.back();
  }

  private recalculateTotals(): void {
    this.totalCreditLimit = this.selectedLoans.reduce((acc, item) => acc + item.offerAmount, 0);
    this.totalOutstandingBalance = this.selectedLoans.reduce((acc, item) => acc + item.outstandingBalance, 0);
    this.totalsListItems = this.adapter.adaptListData(this.totalCreditLimit, this.totalOutstandingBalance);
    this.totalsWidgetConfig = this.adapter.adaptListWidgetConfigData();
  }

  private showConfirmationDialog(selectedLoans: AccountPledgeData[]): void {
    SimpleDialogComponent.show(this.dialog, confirmationDialogData)
      .pipe(untilDestroyed(this))
      .subscribe((actionKey: string) => {
        if (actionKey === CommonActionKey.Confirm) {
          this.pledgeLoans(selectedLoans);
        }
      });
  }

  private pledgeLoans(selectedLoans: AccountPledgeData[]): void {
    this.accountPledgeService
      .pledgeLoans(
        selectedLoans.map(loan => loan.id),
        this.selectedFacilityId
      ).subscribe({
      next: result => {
        if (result?.unsuccessfulLoanIds.length > 0) {
          this.showFailedDialog(selectedLoans.filter(loan => result.unsuccessfulLoanIds.includes(loan.id)));
        } else {
          this.messageService.success("Your selected loans have been successfully pledged.");
          this.filter(true);
        }
      }
    });
  }

  private showFailedDialog(unsuccessfulLoans: AccountPledgeData[]): void {
    this.dialog.open(AccountPledgeFailedDialogComponent, {
      data: {
        unsuccessfulLoans: unsuccessfulLoans
      } as AccountPledgeFailedDialogData,
      closeOnNavigation: true,
    }).afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.filter(true);
      });
  }

  private setDataFromQueryParams(): void {
    const queryParams = this.route.snapshot.queryParams;
    this.queryParams = adaptAccountPledgeParams(queryParams);
    this.selectedFacilityId = +queryParams?.selectedFacilityId;
  }

  private setPaginator(): void {
    this.matPaginator.pageSize = this.queryParams.limit;
    this.matPaginator.pageIndex = this.queryParams.skip / this.queryParams.limit;
  }

  private initialize(): void {
    forkJoin({
      facilities: this.accountPledgeService.getFacilities(true),
      tableData: this.accountPledgeService.getUnpledgedLoans(this.queryParamsService.init(this.queryParams))
    }).subscribe({
      next: (result) => this.setData(result.facilities, result.tableData),
      error: (error) => this.messageService.error(error.message),
    });
  }

  private setData(facilities: Facility[], tableData: AccountPledgeQueryResult): void {
    this.facilities = facilities;
    this.accountPledgeQueryResult = tableData;

    const defaultFacility = this.facilities.find(facility => facility.isDefault);
    this.form.controls.facilityId.setValue(this.selectedFacilityId || defaultFacility.id);
  }

  private filter(shouldFilterSelectedLoans: boolean): void {
    this.queryParams = adaptQueryParams(this.filters, this.matPaginator, this.matSort, this.selectedFacilityId, this.queryParams);

    if (shouldFilterSelectedLoans) {
      this.filterSelectedLoans();
      this.matPaginator.firstPage();
    }

    this.accountPledgeService.getUnpledgedLoans(this.queryParams).subscribe({
      next: (tableData) => this.accountPledgeQueryResult = tableData,
      error: (error) => this.messageService.error(error.message),
      complete: () =>
        this.router.navigate(['account/pledge'], {replaceUrl: true, queryParams: this.queryParams})
    });
  }

  private filterSelectedLoans(): void {
    if(this.selectedLoans?.length <= 0)
      return;

    this.accountPledgeService.getLoansEligibleForFacility({
        ...this.queryParams,
        loanIds: this.selectedLoans.map(loan => loan.id),
      }).pipe(catchError(error => {
        this.messageService.error(error);
        this.selectedLoans = [];
        this.recalculateTotals();
        return EMPTY;
      })).subscribe(result => {
      this.selectedLoans = this.selectedLoans.filter(l => result.loanIds.includes(l.id));
      this.recalculateTotals();
    });
  }

  private actionDispatch(action: AppBarAction): void {
    const actionHandler: (action: AppBarAction) => void =
      this[action.id].bind(this);
    actionHandler(action);
  }

  private setAppBarActions(): void {
    this.appBarActionsService.actions = [];
    this.appBarActionsService.actions.push({
      id: "close",
      label: "CLOSE",
      buttonType: "button",
    });
    this.appBarActionsService.actions.push({
      id: 'save',
      label: 'Save',
      buttonType: 'submit',
      buttonAppearance: 'flat',
      buttonColor: 'primary'
    });
  }

}
